/**
 * Main TickerChart component.
 * Public API with declarative props and imperative methods.
 * Refactored from TickerPriceChart with strict separation between
 * visual layer (React) and processing layer (pure TypeScript).
 *
 * Uses ~utils/ticker-chart/chartApiUtils for API creation to eliminate duplication.
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { classes } from "@bloomberg/media-ui-core";

import styles from "../styles/styles.module.css";
import { Crosshair } from "../overlays/Crosshair";
import { LastVisiblePriceIndicator } from "../overlays/LastVisiblePriceIndicator";
import { InteractionLayer } from "../interaction/InteractionLayer";
import { BaselineLine } from "../overlays/BaselineLine";
import { OhlcvOverlay } from "../overlays/OhlcvOverlay";
import { AnimatedPriceSeries } from "../series/AnimatedPriceSeries";
import { PriceSeries } from "../series/PriceSeries";
import { Tooltip } from "../overlays/Tooltip";
import { VolumeSeries } from "../overlays/VolumeSeries";
import { XAxis } from "../axes/XAxis";
import { YAxis } from "../axes/YAxis";
import { ChartContainer } from "./ChartContainer";
import {
  createChartApi,
  getSeriesColor,
  setChartDebug,
} from "~utils/ticker-chart";
import {
  type ChartType,
  type TickerChartApi,
  type TickerChartProps,
  type TickerSeries,
} from "~types/types";
import { useChartStore, useStoreRef } from "~hooks/ticker-chart";

/**
 * TickerChart - Cross-platform financial charting component.
 * Supports single ticker and multi-ticker comparison modes.
 */
const TickerChartInner = forwardRef<TickerChartApi, TickerChartProps>(
  (props, ref) => {
    const {
      mode,
      height,
      width = "auto",
      padding,
      initialRange,
      interactions,
      children,
      crosshair,
      yAxisPosition = "right",
      showGradient = true,
      formatters,
      onHover,
      onClick,
      realtime,
      dataConfig,
      ariaLabel,
      ariaDescription,
      onReady,
      xAxisTickSize = 4,
      yAxisTickSize = 3,
      debug = false,
      renderTooltip,
      theme = "light",
      viewportEndBuffer,
      animate = false,
      animationDuration,
      intraday,
    } = props;

    // Set global debug state for chart logging
    useEffect(() => {
      setChartDebug(debug);
    }, [debug]);

    // Extract series and assign colors for comparison mode
    // Single mode colors are computed dynamically based on price direction
    const seriesArray = useMemo((): TickerSeries[] => {
      let rawSeries: TickerSeries[];

      if (mode === "single") {
        const singleProps = props as Extract<
          TickerChartProps,
          { mode: "single" }
        >;

        rawSeries = Array.isArray(singleProps.series)
          ? singleProps.series
          : [singleProps.series];

        // Single mode: Don't pre-assign color - computed from price direction
        // in PriceSeries and Crosshair based on visible data
        return rawSeries;
      }

      // Comparison mode: Assign palette colors to distinguish series
      rawSeries = (props as Extract<TickerChartProps, { mode: "comparison" }>)
        .series;

      return rawSeries.map((series, index) => ({
        ...series,
        color: series.color ?? getSeriesColor(index),
      }));
      // props is accessed via type casts but only props.series data matters
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, props.series]);

    // These primitive values don't need useMemo - they're computed once per render
    // and React.memo on TickerPriceChart prevents parent re-renders
    const singleProps =
      mode === "single"
        ? (props as Extract<TickerChartProps, { mode: "single" }>)
        : null;

    const compProps =
      mode === "comparison"
        ? (props as Extract<TickerChartProps, { mode: "comparison" }>)
        : null;

    const chartType: ChartType = singleProps?.chartType ?? "line";
    const volumeEnabled = singleProps?.volume?.enabled ?? false;
    const volumeHeightRatio = singleProps?.volume?.heightRatio ?? 0.2;
    const showLastVisiblePrice =
      singleProps?.showLastVisiblePrice ??
      compProps?.showLastVisiblePrice ??
      false;
    const comparisonConfig = compProps?.comparison;
    const showMultiColorLine = singleProps?.showMultiColorLine ?? false;
    const usePreviousCloseAsBaseline =
      singleProps?.usePreviousCloseAsBaseline ?? false;
    const showBaseline = singleProps?.showBaseline ?? false;
    const showOhlcvOverlay =
      mode === "single" && (chartType === "candlestick" || volumeEnabled);

    // Determine if realtime dot is enabled (for hiding current price dot when both are active)
    const realtimeDotEnabled =
      realtime?.showRealtimeDot ?? realtime?.autoScroll ?? false;

    const store = useChartStore({
      mode,
      chartType,
      theme,
      series: seriesArray,
      width: typeof width === "number" ? width : 800,
      height,
      padding,
      initialViewport: initialRange,
      volumeEnabled,
      volumeHeightRatio,
      yAxisPosition,
      comparisonConfig,
      dataConfig,
      realtime,
      viewportEndBuffer,
      intraday,
      usePreviousCloseAsBaseline,
    });

    const storeRef = useStoreRef(store);

    // Create API using the factory function (eliminates duplication)
    const api = useMemo(() => createChartApi(() => storeRef.current), []);

    // Expose API via ref
    useImperativeHandle(ref, () => api, [api]);

    // Call onReady with API when component mounts
    useEffect(() => {
      if (onReady) {
        onReady(api);
      }
    }, [onReady, api]);

    const layout = store.getLayout();
    const seriesIds = useMemo(
      () => seriesArray.map((s) => s.id),
      [seriesArray]
    );

    const themeClass = theme === "dark" ? styles.themeDark : styles.themeLight;
    const modeClass =
      mode === "single" ? styles.chartSingle : styles.chartComparison;
    const containerClass = classes([modeClass, themeClass]);

    return (
      <ChartContainer
        store={store}
        width={width}
        height={height}
        className={containerClass}
        ariaLabel={ariaLabel ?? `${mode} mode chart`}
        ariaDescription={ariaDescription}
      >
        <svg
          width={layout.width}
          height={layout.height}
          data-component="price-movement-chart-svg"
        >
          {/* Background */}
          <rect
            x={0}
            y={0}
            width={layout.width}
            height={layout.height}
            fill="transparent"
            data-component="price-movement-chart-background"
          />
          {/* Price series */}
          {seriesArray.map((series) =>
            animate ? (
              <AnimatedPriceSeries
                key={`${series.id}-${dataConfig?.interval}`}
                seriesId={series.id}
                chartType={chartType}
                width={layout.priceArea.width}
                height={layout.priceArea.height}
                x={layout.priceArea.x}
                y={layout.priceArea.y}
                duration={animationDuration}
              >
                <PriceSeries
                  seriesId={series.id}
                  chartType={chartType}
                  color={series.color}
                  showGradient={showGradient}
                  showRealtimeDot={
                    realtime?.showRealtimeDot ?? realtime?.autoScroll ?? false
                  }
                  showMultiColorLine={showMultiColorLine}
                />
              </AnimatedPriceSeries>
            ) : (
              <PriceSeries
                key={series.id}
                seriesId={series.id}
                chartType={chartType}
                color={series.color}
                showGradient={showGradient}
                showRealtimeDot={
                  realtime?.showRealtimeDot ?? realtime?.autoScroll ?? false
                }
                showMultiColorLine={showMultiColorLine}
              />
            )
          )}
          {/* Volume series */}
          {volumeEnabled &&
            seriesArray.map((series) => (
              <VolumeSeries key={`volume-${series.id}`} seriesId={series.id} />
            ))}{" "}
          {/* Axes */}
          <XAxis tickCount={xAxisTickSize} formatter={formatters?.xAxis} />
          <YAxis
            position={yAxisPosition}
            type="price"
            tickCount={yAxisTickSize}
            formatter={formatters?.yAxis}
          />
          {mode === "single" && showBaseline && (
            <BaselineLine
              seriesId={seriesArray[0].id}
              currency={seriesArray[0]?.issuedCurrency}
            />
          )}
          {/* Last visible price indicator (controlled by showLastVisiblePrice prop) */}
          {showLastVisiblePrice &&
            seriesArray.map((series) => (
              <LastVisiblePriceIndicator
                key={`last-visible-price-${series.id}`}
                seriesId={series.id}
                showDot={!realtimeDotEnabled}
              />
            ))}
          {/* OHLCV overlay on hover */}
          {showOhlcvOverlay && <OhlcvOverlay seriesId={seriesArray[0].id} />}
          {/* Crosshair */}
          <Crosshair
            showVertical={crosshair?.showVertical}
            showHorizontal={crosshair?.showHorizontal}
            yAxisFormatter={formatters?.yAxis}
          />
          {/* Tooltip - only rendered when renderTooltip callback is provided */}
          {renderTooltip && <Tooltip renderTooltip={renderTooltip} />}
          {/* Interaction layer (must be last for proper event capture) */}
          <InteractionLayer
            seriesIds={seriesIds}
            hoverEnabled={interactions?.hoverEnabled ?? true}
            panEnabled={interactions?.panEnabled ?? true}
            zoomEnabled={interactions?.zoomEnabled ?? true}
            onHover={onHover}
            onClick={onClick}
          />
          {/* consumer content in chart context */}
          {children}
        </svg>
      </ChartContainer>
    );
  }
);

TickerChartInner.displayName = "TickerChart";

/**
 * Memoized TickerChart to prevent re-renders when props haven't changed.
 * Critical for performance when parent components re-render frequently.
 */
export const TickerChart = React.memo(TickerChartInner);
